import { Component, computed, inject, Input } from "@angular/core";
import { Router } from "@angular/router";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { FREE, SubscriptionTier } from "@bitwarden/web-vault/app/models/types/subscription.type";
import { StorageLimitPipe } from "@bitwarden/web-vault/app/pipes/storage-limit.pipe";
import { GlossRouteEnum } from "@bitwarden/web-vault/app/models/enum/routes.enum";
import { UpgradeNavigationService } from "@bitwarden/web-vault/app/services/subscription/upgrade-navigation.service";

const PREMIUM: SubscriptionTier = "premium";

@Component({
  selector: "app-storage",
  templateUrl: "storage.component.html",
  providers: [StorageLimitPipe],
})
export class StorageComponent {
  @Input() closeDialog: CallableFunction;
  private router = inject(Router);
  private upgradeNavigation = inject(UpgradeNavigationService);
  private i18nService: I18nService = inject(I18nService);
  protected userStoreService = inject(UserStoreService);
  protected storageLimitPipe = inject(StorageLimitPipe);
  // activeSubscription() is null before it loads and after logout (and this component also
  // opens as a dialog, not only behind the subscription routes). Null reads as the free tier,
  // so the header doesn't render "On the  bundle" and the storage warning still applies.
  protected currentSubscriptionTier = computed(() => {
    return this.userStoreService.subscription.activeSubscription()?.tier ?? FREE;
  });
  protected isPremium = computed(() => this.currentSubscriptionTier() === PREMIUM);
  protected storageUsage = this.userStoreService.user.usage;

  protected cabinetPct = computed(() => this.toPct(this.storageUsage().s3InUsed));
  protected accountsPct = computed(() => this.toPct(this.storageUsage().vaultInUsed));
  protected totalPct = computed(() => this.toPct(this.storageUsage().totalInUsed));
  protected freeInUsed = computed(() =>
    Math.max(this.storageUsage().limit - this.storageUsage().totalInUsed, 0),
  );
  protected showStorageWarning = computed(() => !this.isPremium() && this.totalPct() >= 80);

  // When usage exceeds limit, divide by actual usage instead so segments stay
  // proportional and their widths still sum to 100% (independent per-segment
  // caps at limit would let both segments hit 100% and overflow the bar).
  private usageDenom = computed(() =>
    Math.max(this.storageUsage().limit, this.storageUsage().totalInUsed),
  );

  selectBundleButton = new ActionButton({
    text: this.i18nService.t("selectBundle"),
    class: "!tw-w-3/12 sm:!tw-w-full tw-bg-primary tw-text-base tw-text-white tw-font-medium",
    isEnabled: true,
    onClick: this.goToSubscription.bind(this),
  });

  manageCabinetButton = new ActionButton({
    text: this.i18nService.t("manageFilesInCabinet"),
    class:
      "!tw-h-10 !tw-bg-transparent tw-border tw-border-solid tw-border-dt-custom-teal-11 !tw-text-dt-custom-teal-11 tw-text-sm",
    isEnabled: true,
    onClick: this.goToCabinet.bind(this),
  });

  manageAccountsButton = new ActionButton({
    text: this.i18nService.t("manageAccounts"),
    class:
      "!tw-h-10 !tw-bg-transparent tw-border tw-border-solid tw-border-dt-custom-teal-11 !tw-text-dt-custom-teal-11 tw-text-sm",
    isEnabled: true,
    onClick: this.goToAccounts.bind(this),
  });

  goToSubscription() {
    void this.upgradeNavigation.goToUpgrade();
  }

  goToCabinet() {
    this.router.navigate([GlossRouteEnum.cabinet]);
  }

  goToAccounts() {
    this.router.navigate([GlossRouteEnum.account]);
  }

  private toPct(part: number): number {
    const denom = this.usageDenom();
    return denom ? Math.min((part / denom) * 100, 100) : 0;
  }
}
